import Vue from 'vue';

import {AuthClient, CredentialProvider, DisposableTokenScopes, CacheClient, CacheGetResponse, CacheSetAddElementResponse,
        CacheSetRemoveElementResponse, ExpiresIn, Configurations} from '@gomomento/sdk-web';

const apiKey = process.env.VUE_APP_MOMENTO_API_KEY;

// PHASE 2 PREVENTION: Follow-up cache timeout (10 minutes)
const FOLLOWUP_CACHE_TTL_SECONDS = 10 * 60; // 10 minutes

export default {
    namespaced: true,
    state: {
        momento: null,
        token: null,
        set: null,
        // PHASE 2 PREVENTION: Track active follow-up sessions for cleanup
        activeFollowupSessions: new Map(), // patientID -> { timestamp, payload }
    },

    getters : {        
        // PHASE 2 PREVENTION: Get active follow-up sessions for monitoring
        getActiveFollowupSessions: (state) => {
            return Array.from(state.activeFollowupSessions.entries()).map(([patientId, session]) => ({
                patientId,
                startTime: session.timestamp,
                duration: Date.now() - session.timestamp,
                payload: session.payload
            }));
        },
        
        // PHASE 2 PREVENTION: Check if a patient is being followed up with
        isPatientBeingFollowedUp: (state) => (patientId) => {
            return state.activeFollowupSessions.has(patientId);
        }
    },

    mutations: {        
        setMomento(state, payload) {
            state.momento = payload;
        },

        setToken(state, payload) {
            state.token = payload;
        },

        addValueToSet(state, payload) {
            state.set = payload;
        },
        
        // PHASE 2 PREVENTION: Track follow-up session start
        addActiveFollowupSession(state, { patientId, payload }) {
            state.activeFollowupSessions.set(patientId, {
                timestamp: Date.now(),
                payload: payload
            });
        },
        
        // PHASE 2 PREVENTION: Remove follow-up session tracking
        removeActiveFollowupSession(state, patientId) {
            state.activeFollowupSessions.delete(patientId);
        },
        
        // PHASE 2 PREVENTION: Clear all active sessions (for emergency cleanup)
        clearAllActiveFollowupSessions(state) {
            state.activeFollowupSessions.clear();
        }
    },

    actions: {
        async initialize({commit, rootState, dispatch}, payload) {
            if(rootState.cache.momento === null) {
                // PHASE 2 PREVENTION: Use shorter TTL for follow-up cache entries
                const momentoClient = await CacheClient.create({
                    configuration: Configurations.Browser.latest(),
                    credentialProvider: CredentialProvider.fromString({apiKey: apiKey}),
                    defaultTtlSeconds: FOLLOWUP_CACHE_TTL_SECONDS, // 10 minutes
                });
                commit('setMomento', momentoClient);
            }
        },

        async getDisposableToken({commit, rootState, dispatch}, payload) {
            const authClient = new AuthClient({});
            const tokenResponse = await authClient.generateDisposableToken(
                'teamio',
                ExpiresIn.seconds(FOLLOWUP_CACHE_TTL_SECONDS) // Use consistent TTL (10 minutes)
            );
            commit('setToken', tokenResponse);
        },

        // Enhanced addValueToSet with TTL and better error handling
        async addValueToSet({ commit, rootState, dispatch }, payload) {
            // Track the session start
            const patientId = payload.element;
            commit('addActiveFollowupSession', { patientId, payload });

            let cleanupExecuted = false;
            const executeCleanup = () => {
                if (!cleanupExecuted) {
                    cleanupExecuted = true;
                    commit('removeActiveFollowupSession', patientId);
                }
            };

            try {
                if(rootState.cache.momento === null) {
                    await dispatch('cache/initialize', null, {root: true});
                }

                const res = await rootState.cache.momento.setContainsElement(payload.cacheName, payload.cacheSet, payload.element);

                if(res.type === 'Miss' || (res.type === 'Hit' && !res._containsElement)) {
                    // Add element using default TTL from client initialization
                    const result = await rootState.cache.momento.setAddElement(
                        payload.cacheName,
                        payload.cacheSet,
                        payload.element
                    );

                    switch (result.type) {
                        case CacheSetAddElementResponse.Success:
                            break;
                        case CacheSetAddElementResponse.Error:
                            executeCleanup();
                            throw new Error(
                                `An error occurred while attempting to call cacheSetAddElement on set ${payload.cacheSet} in cache '${payload.cacheName}': ${result.errorCode()}: ${result.toString()}`
                            );
                    }
                } else {
                    executeCleanup();
                    throw new Error(`exists`);
                }
            } catch (error) {
                executeCleanup();
                if (error.message !== 'exists') {
                    console.error('[CACHE] Error in addValueToSet:', error);
                }
                throw error;
            }
        },

        // Enhanced removeValueFromSet with better error handling
        async removeValueFromSet({commit, rootState, dispatch}, payload) {
            const patientId = payload.element;

            try {
                if(rootState.cache.momento === null) {
                    await dispatch('cache/initialize', null, {root: true});
                }

                const result = await rootState.cache.momento.setRemoveElement(payload.cacheName, payload.cacheSet, payload.element);

                switch (result.type) {
                    case CacheSetRemoveElementResponse.Success:
                        break;
                    case CacheSetRemoveElementResponse.Error:
                        console.error('[CACHE] Error removing element:', result.errorCode(), result.toString());
                        throw new Error(
                            `An error occurred while attempting to call cacheSetRemoveElement on set ${payload.cacheSet} in cache '${payload.cacheName}': ${result.errorCode()}: ${result.toString()}`
                        );
                }
            } catch(err) {
                console.error('[CACHE] Error in removeValueFromSet:', err);
                throw err;
            } finally {
                // Always remove from local tracking regardless of cache operation result
                commit('removeActiveFollowupSession', patientId);
            }
        },

        // Emergency cleanup for all active sessions
        async emergencyCleanupAllSessions({ commit, rootState, dispatch, getters }) {
            const activeSessions = getters.getActiveFollowupSessions;

            const cleanupPromises = activeSessions.map(async (session) => {
                try {
                    await dispatch('removeValueFromSet', session.payload);
                } catch (error) {
                    console.error('[CACHE] Emergency cleanup failed for patient:', error);
                }
            });

            await Promise.allSettled(cleanupPromises);
            commit('clearAllActiveFollowupSessions');
        },

        // Cleanup expired sessions based on TTL
        async cleanupExpiredSessions({ commit, getters, dispatch }) {
            const activeSessions = getters.getActiveFollowupSessions;
            const now = Date.now();
            const ttlMs = FOLLOWUP_CACHE_TTL_SECONDS * 1000;

            const expiredSessions = activeSessions.filter(session =>
                (now - session.startTime) > ttlMs
            );

            if (expiredSessions.length > 0) {
                for (const session of expiredSessions) {
                    try {
                        await dispatch('removeValueFromSet', session.payload);
                    } catch (error) {
                        console.error('[CACHE] Failed to cleanup expired session:', error);
                    }
                }
            }
        },

        async setContainsElement({commit, rootState}, payload) {
            try {
                const result = await rootState.momento.setContainsElement(payload);
            } catch (error) {
                console.error('[CACHE] Error in setContainsElement:', error);
            }
        }
    }

}